import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { POI_TYPES } from '@modules/roadscope/constants/poiTypes';
import { COLLECTIONS } from '@utils/database/collections'; 
import { logError, logInfo } from '@utils/logger';
import { collection, query, where, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../../../../config/firebase';
import { Toast } from '../../../../components/common/Toast';
import { useLocationTracking } from '@hooks/useLocationTracking';
import { useNavigate } from 'react-router-dom';
import { useSurvey } from '../../../../contexts/SurveyContext';
import { savePendingData } from '../../../../utils/offlineStorage';
import { generatePOIId } from '@utils/idGenerators';
import { RoadPOIModal } from './modals/RoadPOIModal';
import { IntersectionPOIModal } from './modals/IntersectionPOIModal';
import { BridgePOIModal } from './modals/BridgePOIModal';
import { PowerLinePOIModal } from './modals/PowerLinePOIModal';
import { TreesPOIModal } from './modals/TreesPOIModal';
import { SignalizationPOIModal } from './modals/SignalizationPOIModal';
import { RailroadPOIModal } from './modals/RailroadPOIModal';
import { CustomPOIModal } from './modals/CustomPOIModal';

// Map POI types to their specific modals
const POI_MODALS = {
  Road: RoadPOIModal,
  Intersection: IntersectionPOIModal,
  Bridge: BridgePOIModal,
  PowerLine: PowerLinePOIModal,
  Trees: TreesPOIModal,
  Signalization: SignalizationPOIModal,
  Railroad: RailroadPOIModal,
  Custom: CustomPOIModal
};

const HighPolePOISelector = ({ onClose, onSave }) => {
  const { userLocation } = useLocationTracking();
  const navigate = useNavigate();
  const { activeSurvey } = useSurvey();
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [poiInfo, setPoiInfo] = useState({
    id: null,
    coordinates: userLocation ? {
      latitude: userLocation.lat,
      longitude: userLocation.lng
    } : null,
    address: 'Getting address...'
  });

  // Generate POI ID when type is selected
  useEffect(() => {
    if (selectedType) {
      const id = generatePOIId('HP', selectedType, 1, Date.now());
      setPoiInfo(prev => ({
        ...prev,
        id: id
      }));
    }
  }, [selectedType]);
  // Update POI info when location changes
  useEffect(() => {
    if (userLocation) {
      setPoiInfo(prev => ({
        ...prev,
        coordinates: {
          latitude: userLocation.lat,
          longitude: userLocation.lng
        }
      }));
    }
  }, [userLocation]);

  // Get nearest address when coordinates change
  useEffect(() => {
    const getAddress = async () => {
      if (poiInfo.coordinates) {
        try {
          const address = await getNearestAddress(
            poiInfo.coordinates.latitude,
            poiInfo.coordinates.longitude
          );
          setPoiInfo(prev => ({
            ...prev,
            address: address || 'Address not found'
          }));
        } catch (error) {
          console.error('Failed to get address:', error);
          setPoiInfo(prev => ({
            ...prev,
            address: 'Failed to get address'
          }));
        }
      }
    };
    getAddress();
  }, [poiInfo.coordinates]);
  const handleSave = async (data, selectedType) => {
    try {
      setSaving(true);
      setError(null);
      setToast(null);

      if (!activeSurvey) {
        throw new Error('No active survey selected');
      }

      // Ensure we have valid coordinates
      if (!userLocation?.lat || !userLocation?.lng) {
        throw new Error('Location coordinates are required');
      }

      // Add coordinates to POI data
      const poiData = {
        ...data,
        coordinates: {
          latitude: userLocation.lat,
          longitude: userLocation.lng
        }
      };

      // Get POI count for sequence number
      const poiCount = await getDocs(query(
        collection(db, COLLECTIONS.POIS),
        where('surveyId', '==', activeSurvey.id)
      )).then(snap => snap.size);

      // Generate POI ID using the survey type and sequence
      const poiId = generatePOIId(
        'HP', // High Pole survey type
        selectedType,
        1, // Route number (default to 1 for now)
        poiCount + 1 // Sequence number
      );

      // Save POI to Firestore using the generated ID
      const poiRef = doc(db, COLLECTIONS.POIS, poiId);
      await setDoc(poiRef, {
        ...poiData,
        id: poiId,
        type: selectedType,
        surveyId: activeSurvey.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      if (!navigator.onLine) {
        await savePendingData(COLLECTIONS.POIS, poiData);
        logInfo('POI saved for offline sync');
        setToast({
          type: 'success',
          message: 'POI saved for offline sync',
          zIndex: 1000
        });
        setTimeout(() => onClose(), 1500);
        return { success: true };
      } else {
        await onSave?.(poiData, selectedType);
        logInfo('POI saved successfully');
        setToast({
          type: 'success',
          message: 'POI saved successfully',
          zIndex: 1000
        });
        setTimeout(() => onClose(), 1500);
        return { success: true };
      }

      if (hasValidLocation) {
        setTimeout(() => onClose(), 1500);
        return { success: true };
      } else {
        // Prepare navigation state
        const navigationState = {
          placePOI: poiId,
          poiData: {
            id: poiData.id,
            type: poiData.type,
            surveyId: poiData.surveyId,
            measurements: poiData.measurements,
            notes: poiData.notes,
            photos: poiData.photos,
            createdAt: poiData.createdAt,
            updatedAt: poiData.updatedAt
          }
        };

        // Navigate to map with POI data
        requestAnimationFrame(() => {
          navigate('/map', {
            state: navigationState,
            replace: true
          });
        });
        
        setToast({
          type: 'info',
          message: 'Please place the POI on the map',
          zIndex: 1000
        });

        onClose();
        return { success: true, requiresPlacement: true };
      }
    } catch (error) {
      const errorMessage = error?.message || 'Failed to save POI';
      logError('Failed to save POI:', error);
      setError(errorMessage);
      if (!error.requiresPlacement) {
        setToast({
          type: 'error',
          message: errorMessage,
          zIndex: 1000
        });
      }
      return { success: false, error: errorMessage };
    } finally {
      setSaving(false);
    }
  };

  // Render type-specific modal if type is selected
  if (selectedType) {
    const ModalComponent = POI_MODALS[selectedType];
    if (ModalComponent) {
      return (
        <ModalComponent
          poiInfo={poiInfo}
          type={selectedType}
          onClose={onClose}
          onSave={(data) => handleSave(data, selectedType)}
          onBack={() => setSelectedType(null)}
        />
      );
    }
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-[300] overflow-y-auto">
      <div className="bg-[#2A2B2E] rounded-lg shadow-lg w-full max-w-md">
        {/* Toast Notifications */}
        <div className="fixed top-4 right-4 z-[1000]">
          {toast && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast(null)}
            />
          )}
        </div>

        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Select High Pole POI Type</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        
        {error && (
          <div className="p-4 mb-4 bg-red-500/10 text-red-400 rounded-lg">
            {error}
          </div>
        )}

        <div className="p-4">
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(POI_TYPES).map(([type, config]) => {
              // Only show POI types that have a specific modal
              if (!POI_MODALS[type] && type !== 'Custom') return null;

              const Icon = config.icon;
              return (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  disabled={saving}
                  className="flex flex-col items-center justify-center p-4 bg-gray-700/50 rounded-xl hover:bg-gray-600/50 transition-colors"
                >
                  <div 
                    className="w-12 h-12 rounded-full mb-2 flex items-center justify-center"
                    style={{ backgroundColor: `${config.color}20`, color: config.color }}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <span className="text-sm font-medium text-white">{type}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HighPolePOISelector;

export { HighPolePOISelector }